import { useMutation } from '@tanstack/react-query'
import {
  type PreferencesInput,
  type PreferenceValue,
  type UserPreferencesInput,
} from '@/features/account/services/Preferences/types'
import { apiPut } from '@/features/shared/utils/dataFetching'
import { preferences } from '@/features/account/services/Preferences/endpointHelpers'
import { useUser } from '@/features/account/services/User/hooks'
import { communicationPreferencesQueryOptions } from '@/features/account/services/Preferences/queries'
import { getOptimisticCommunications } from '@/features/account/services/Preferences/utils'
import { type ConsentPurpose, type ConsentPurposesAPI } from '@/lib/transcend/useConsentManager'
import { logError } from '@/features/shared/utils/logger'

type MutationVars = UserPreferencesInput & {
  skipOptimistic?: boolean
}

export const useMutationPreferences = () => {
  const userId = useUser()?.id ?? 0
  const queryKey = communicationPreferencesQueryOptions(userId).queryKey

  return useMutation({
    mutationFn: ({ skipOptimistic, ...preferencesInput }: MutationVars) =>
      apiPut<void>({
        config: { url: preferences(userId), data: preferencesInput },
        fetcherName: 'useMutationPreferences',
      }),
    onMutate: async ({ communications, skipOptimistic }, context) => {
      if (skipOptimistic) return

      // Cancel any outgoing refetches
      await context.client.cancelQueries({ queryKey })

      // Snapshot of previous preferences
      const previousPreferences = context.client.getQueryData(queryKey)

      const { communications: prevCommunications } = previousPreferences ?? {}

      // Optimistically update with the new communication preferences
      context.client.setQueryData(queryKey, {
        ...previousPreferences,
        communications: {
          ...prevCommunications,
          ...getOptimisticCommunications(prevCommunications, communications),
        },
      })

      // Return snapshot so we can revert upon failure
      return { previousPreferences }
    },
    // If the mutation fails, roll back to previous preferences
    onError: (_error, _variables, onMutateResult, context) => {
      if (onMutateResult?.previousPreferences) {
        context.client.setQueryData(queryKey, onMutateResult.previousPreferences)
      }
    },
    onSuccess: (_data, _variables, _, context) => {
      return context.client.invalidateQueries({ queryKey })
    },
  })
}

export const NO_CONSENT_PURPOSES_ERROR = 'Consent purposes are not available'

export const useMutationConsentPreferences = () => {
  return useMutation({
    mutationFn: ({
      userId,
      purposes,
    }: {
      userId: number
      purposes: ConsentPurposesAPI | undefined
    }) => {
      if (!purposes) {
        logError(NO_CONSENT_PURPOSES_ERROR)
        throw new Error(NO_CONSENT_PURPOSES_ERROR)
      }
      return apiPut<void>({
        config: {
          url: preferences(userId),
          data: {
            consent: [
              { id: 'advertising', value: Boolean(purposes['Advertising']) },
              { id: 'analytics', value: Boolean(purposes['Analytics']) },
              { id: 'functional', value: Boolean(purposes['Functional']) },
            ] satisfies PreferencesInput<PreferenceValue<ConsentPurpose>>[],
          },
        },
        fetcherName: 'useMutationConsentPreferences',
      })
    },
    retry: (failureCount, error) => {
      return failureCount < 3 && error.message !== NO_CONSENT_PURPOSES_ERROR
    },
  })
}
